import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { useIntl } from 'react-intl';
import { Box, Stack, Tooltip } from '@mui/material';
import './styles/sidenavpro.css';
import { useMaterialUIController } from 'context';
import AccountPopover from 'components/Navbars/PortalNavbar/AccountPopover';
import MDBox from 'components/MDBox';
import logoAiIcon from 'assets/images/logo-dina-icon-white.svg';
import logoAi from 'assets/images/logo-dina-white.svg';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';

function SidenavPro({ routes }) {
  const { formatMessage: msg } = useIntl();
  const location = useLocation();
  const { controller } = useMaterialUIController();
  const { miniSidenav } = controller;

  const [collapsed, setCollapsed] = useState(miniSidenav);

  const handleMouseEnter = () => setCollapsed(routes.length === 0);
  const handleMouseLeave = () => setCollapsed(true);

  const generatePath = (currentPath, newPath) => {
    const segments = currentPath.split('/').filter(Boolean);
    segments.pop();
    return `/${segments.join('/')}/${newPath}`;
  };

  const { getValue: conf } = useConf(useGetConfigurationQuery);

  const renderRoutes = routes.map(
    ({ type, visibleInMenu = true, name, icon, key, href, path, onClick = () => {} }) => {
      const label = msg({ id: `page.${key}`, defaultMessage: name });
      const finalPath = generatePath(location.pathname, path);

      if (type === 'collapse' && visibleInMenu) {
        const menuItem = (
          <MenuItem
            icon={icon}
            active={location.pathname.includes(finalPath)}
            component={
              href ? (
                <a href={href} target="_blank" rel="noreferrer" onClick={onClick} />
              ) : (
                <NavLink to={finalPath} onClick={onClick} />
              )
            }
          >
            <span
              style={{
                opacity: collapsed ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out',
                whiteSpace: 'nowrap'
              }}
            >
              {label}
            </span>
          </MenuItem>
        );

        // Only wrap with Tooltip when collapsed
        return collapsed ? (
          <Tooltip key={key} title={label} placement="right">
            <div>{menuItem}</div>
          </Tooltip>
        ) : (
          <div key={key}>{menuItem}</div>
        );
      }
      return null;
    }
  );

  return (
    <Sidebar
      collapsed={collapsed}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        background: 'linear-gradient(to top, #1f1355, #088aa9)',
        backgroundColor: 'transparent',
        height: '100vh'
      }}
    >
      <Stack
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          height: '100%',
          flexDirection: 'column'
        }}
      >
        <MDBox
          component="img"
          src={collapsed ? logoAiIcon : logoAi}
          alt="Logo"
          sx={{
            maxHeight: '50px',
            minHeight: '50px',
            width: '100%',
            paddingTop: '10px'
          }}
        />

        <Menu>{renderRoutes}</Menu>

        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            paddingLeft: '7px'
          }}
        >
          <AccountPopover />
          {!collapsed ? (
            <Box
              sx={{
                opacity: collapsed ? 0 : 1,
                transition: 'opacity 0.9s ease-in-out',
                whiteSpace: 'nowrap'
              }}
            >
              Dina {conf(`app.version`)}
            </Box>
          ) : null}
        </Box>
      </Stack>
    </Sidebar>
  );
}

SidenavPro.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default SidenavPro;
